/**
 * Lightweight snow effect for image banners
 * Creates a canvas overlay with falling snow particles
 */
class SnowEffect {
  constructor(container, options = {}) {
    this.container = container;
    this.canvas = null;
    this.ctx = null;
    this.flakes = [];
    this.animationId = null;
    
    // Configuration with defaults
    this.config = {
      enabled: options.enabled !== false,
      flakeCount: options.flakeCount || 100,
      minSize: options.minSize || 1,
      maxSize: options.maxSize || 3,
      minSpeed: options.minSpeed || 1,
      maxSpeed: options.maxSpeed || 2,
      drift: options.drift || 0.3,
      opacity: options.opacity || 0.8,
      color: options.color || '#FFFFFF'
    };
    
    if (this.config.enabled) {
      this.init();
    }
  }
  
  init() {
    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'snow-effect-canvas';
    this.canvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1;
    `;
    
    // Insert canvas after banner media but before content
    const media = this.container.querySelector('.banner__media');
    if (media) {
      media.parentNode.insertBefore(this.canvas, media.nextSibling);
    } else {
      this.container.insertBefore(this.canvas, this.container.firstChild);
    }
    
    this.ctx = this.canvas.getContext('2d');
    this.resize();
    this.createFlakes();
    this.animate();
    
    // Handle resize
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => this.resize());
      resizeObserver.observe(this.container);
    }
    
    // Fallback for older browsers
    window.addEventListener('resize', () => this.resize());
  }
  
  resize() {
    if (!this.canvas) return;
    
    const rect = this.container.getBoundingClientRect();
    this.canvas.width = rect.width;
    this.canvas.height = rect.height;
    
    // Recreate flakes if canvas size changed significantly
    if (this.flakes.length === 0 || Math.abs(this.canvas.width - (this.lastWidth || 0)) > 50) {
      this.createFlakes();
    }
    this.lastWidth = this.canvas.width;
  }
  
  createFlakes() {
    this.flakes = [];
    const count = Math.min(this.config.flakeCount, Math.floor((this.canvas.width * this.canvas.height) / 5000));
    
    for (let i = 0; i < count; i++) {
      this.flakes.push(this.createFlake());
    }
  }
  
  createFlake() {
    // Start flakes at random positions from above viewport to within viewport
    // This makes it look like they've already been falling when page loads
    const startRange = this.canvas.height * 2; // Allow flakes to start up to 2x viewport height above
    return {
      x: Math.random() * this.canvas.width,
      y: Math.random() * startRange - this.canvas.height, // Random position from above to within viewport
      radius: Math.random() * (this.config.maxSize - this.config.minSize) + this.config.minSize,
      speed: Math.random() * (this.config.maxSpeed - this.config.minSpeed) + this.config.minSpeed,
      drift: (Math.random() - 0.5) * this.config.drift,
      opacity: Math.random() * 0.3 + (this.config.opacity - 0.3)
    };
  }
  
  animate() {
    if (!this.canvas || !this.ctx) return;
    
    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Add new flakes occasionally
    if (this.flakes.length < this.config.flakeCount && Math.random() > 0.7) {
      this.flakes.push(this.createFlake());
    }
    
    // Update and draw flakes
    this.ctx.fillStyle = this.config.color;
    
    for (let i = this.flakes.length - 1; i >= 0; i--) {
      const flake = this.flakes[i];
      
      // Update position
      flake.y += flake.speed;
      flake.x += flake.drift;
      
      // Reset if off screen
      if (flake.y > this.canvas.height) {
        flake.y = -10;
        flake.x = Math.random() * this.canvas.width;
      }
      
      // Wrap horizontally
      if (flake.x < 0) flake.x = this.canvas.width;
      if (flake.x > this.canvas.width) flake.x = 0;
      
      // Draw flake
      this.ctx.globalAlpha = flake.opacity;
      this.ctx.beginPath();
      this.ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    this.ctx.globalAlpha = 1;
    this.animationId = requestAnimationFrame(() => this.animate());
  }
  
  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    this.flakes = [];
  }
}

// Store instances to prevent duplicates and allow cleanup
const snowEffectInstances = new Map();

// Auto-initialize for elements with data-snow-effect attribute
function initSnowEffects() {
  const snowContainers = document.querySelectorAll('[data-snow-effect]');
  snowContainers.forEach(container => {
    // Clean up existing instance if it exists
    const existingInstance = snowEffectInstances.get(container);
    if (existingInstance) {
      existingInstance.destroy();
      snowEffectInstances.delete(container);
    }
    
    const config = {
      enabled: container.dataset.snowEnabled !== 'false',
      flakeCount: parseInt(container.dataset.snowFlakeCount) || 100,
      minSize: parseFloat(container.dataset.snowMinSize) || 1,
      maxSize: parseFloat(container.dataset.snowMaxSize) || 3,
      minSpeed: parseFloat(container.dataset.snowMinSpeed) || 1,
      maxSpeed: parseFloat(container.dataset.snowMaxSpeed) || 2,
      drift: parseFloat(container.dataset.snowDrift) || 0.3,
      opacity: parseFloat(container.dataset.snowOpacity) || 0.8,
      color: container.dataset.snowColor || '#FFFFFF'
    };
    
    const instance = new SnowEffect(container, config);
    snowEffectInstances.set(container, instance);
  });
}

// Handle both DOMContentLoaded and already-loaded cases
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSnowEffects);
} else {
  initSnowEffects();
}

// Listen for Shopify theme customizer section updates
document.addEventListener('shopify:section:load', function(event) {
  // Re-initialize snow effects when section is loaded/updated in customizer
  if (event.detail && event.detail.sectionId) {
    const section = document.getElementById('Banner-' + event.detail.sectionId);
    if (section && section.hasAttribute('data-snow-effect')) {
      // Small delay to ensure data attributes are updated
      setTimeout(initSnowEffects, 100);
    }
  } else {
    // Fallback: re-initialize all if section ID not available
    setTimeout(initSnowEffects, 100);
  }
});

// Also listen for general section updates (some customizer versions use this)
document.addEventListener('shopify:section:reorder', function() {
  setTimeout(initSnowEffects, 100);
});

// Watch for data attribute changes and new elements (for when customizer updates settings)
if (window.MutationObserver) {
  let attributeObserver = null;
  let domObserver = null;
  
  function setupAttributeObserver() {
    if (attributeObserver) {
      attributeObserver.disconnect();
    }
    
    attributeObserver = new MutationObserver(function(mutations) {
      let shouldReinit = false;
      mutations.forEach(function(mutation) {
        if (mutation.type === 'attributes' && 
            mutation.target.hasAttribute('data-snow-effect') &&
            mutation.attributeName && 
            mutation.attributeName.startsWith('data-snow-')) {
          shouldReinit = true;
        }
      });
      
      if (shouldReinit) {
        // Debounce re-initialization
        clearTimeout(window.snowEffectReinitTimeout);
        window.snowEffectReinitTimeout = setTimeout(function() {
          initSnowEffects();
          setupAttributeObserver(); // Re-setup to catch new containers
        }, 150);
      }
    });
    
    // Observe all existing containers
    const snowContainers = document.querySelectorAll('[data-snow-effect]');
    snowContainers.forEach(container => {
      attributeObserver.observe(container, {
        attributes: true,
        attributeFilter: [
          'data-snow-enabled',
          'data-snow-flake-count',
          'data-snow-min-size',
          'data-snow-max-size',
          'data-snow-min-speed',
          'data-snow-max-speed',
          'data-snow-drift',
          'data-snow-opacity',
          'data-snow-color'
        ]
      });
    });
  }
  
  // Watch for new elements being added to DOM
  domObserver = new MutationObserver(function(mutations) {
    let hasNewContainers = false;
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) { // Element node
          if (node.hasAttribute && node.hasAttribute('data-snow-effect')) {
            hasNewContainers = true;
          }
          // Also check children
          if (node.querySelectorAll) {
            const containers = node.querySelectorAll('[data-snow-effect]');
            if (containers.length > 0) {
              hasNewContainers = true;
            }
          }
        }
      });
    });
    
    if (hasNewContainers) {
      setTimeout(function() {
        initSnowEffects();
        setupAttributeObserver();
      }, 100);
    }
  });
  
  // Start observing when DOM is ready
  function startObserving() {
    setupAttributeObserver();
    
    // Observe document body for new elements
    domObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
}

